import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import AnimeCard from "@/components/feature/AnimeCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EpisodeList from "./components/EpisodeList";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Detail() {
  const { id = "" } = useParams();
  const anime = animeList.find((a) => a.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [currentEpisode, setCurrentEpisode] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);

  useDocumentTitle(
    anime ? `${anime.title}全集详情与更新 - 动漫网站` : "动漫详情 - 动漫网站",
    anime
      ? `${anime.title}全集在线观看，${anime.synopsis.slice(0, 60)}……`
      : "动漫详情页"
  );

  const related = useMemo(() => {
    if (!anime) return [];
    return animeList
      .filter((a) => a.id !== anime.id && (a.category === anime.category || a.tags.some((t) => anime.tags.includes(t))))
      .slice(0, 4);
  }, [anime]);

  if (!anime) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-foreground-400" />
        </span>
        <h1 className="font-heading font-semibold text-xl text-foreground-900 mt-4">未找到该动漫</h1>
        <p className="text-sm text-foreground-500 mt-2">资源可能已下线，去分类页看看其他作品吧</p>
        <Link
          to="/category/hot-blood"
          className="inline-block mt-5 h-10 px-5 leading-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600"
        >
          浏览动漫分类
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(anime.id);

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "动漫分类", to: "/category/hot-blood" },
          { label: anime.category, to: `/search?q=${encodeURIComponent(anime.category)}` },
          { label: anime.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:gap-10">
        <div className="w-full max-w-[320px] mx-auto lg:mx-0">
          <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200 bg-background-200">
            <img
              src={anime.cover}
              alt={`${anime.title} 动漫封面`}
              title={`${anime.title} - 动漫全集`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button
              onClick={() => setShowPlayer(true)}
              className="h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </button>
            <button
              onClick={() => toggleFavorite(anime.id)}
              className={`h-11 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap border ${
                favorited
                  ? "bg-primary-50 border-primary-300 text-primary-600"
                  : "bg-background-100 border-background-200 text-foreground-700 hover:border-primary-300"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
              {favorited ? "已收藏" : "收藏"}
            </button>
          </div>
          <p className="text-xs text-foreground-400 text-center mt-3 flex items-center justify-center gap-1">
            <i className="ri-information-line" />
            播放功能待接入接口演示，当前为演示状态
          </p>
        </div>

        <div className="min-w-0">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
            {anime.title}
          </h1>

          <div className="flex items-center flex-wrap gap-2 mt-4">
            <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
              {anime.status}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
              {anime.category}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs">
              {anime.year} 年
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs">
              {anime.region}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs flex items-center gap-1">
              <i className="ri-star-fill text-primary-500" />
              {anime.rating.toFixed(1)} 分
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            {[
              { label: "总集数", value: `${anime.episodes} 集` },
              { label: "更新至", value: `第 ${anime.latestEpisode} 集` },
              { label: "热度", value: anime.popularity.toLocaleString() },
              { label: "更新时间", value: anime.updatedAt },
            ].map((stat) => (
              <div key={stat.label} className="p-3.5 rounded-lg bg-background-100 border border-background-200/70">
                <p className="text-xs text-foreground-400">{stat.label}</p>
                <p className="font-heading font-semibold text-foreground-950 mt-1 text-sm md:text-base">{stat.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-7">
            <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-2.5">剧情简介</h2>
            <p className="text-sm md:text-[15px] text-foreground-700 leading-relaxed">{anime.synopsis}</p>
          </div>

          <div className="mt-6">
            <h2 className="font-heading font-semibold text-sm text-foreground-950 mb-2.5">标签</h2>
            <div className="flex flex-wrap gap-2">
              {anime.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section className="mt-10 md:mt-14">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-bold text-xl text-foreground-950 flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-play-list-line text-primary-600" />
            </span>
            播放集数列表
          </h2>
          <span className="text-xs text-foreground-400 hidden sm:block">共 {anime.episodes} 集 · 演示播放</span>
        </div>
        <EpisodeList
          episodeTitles={anime.episodeTitles}
          currentEpisode={currentEpisode}
          onPlay={(index) => {
            setCurrentEpisode(index);
            setShowPlayer(true);
          }}
        />
      </section>

      {related.length > 0 && (
        <section className="mt-10 md:mt-14">
          <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5 flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-magic-line text-primary-600" />
            </span>
            相关推荐
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.map((item) => (
              <AnimeCard key={item.id} anime={item} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 md:mt-14">
        <h2 className="font-heading font-semibold text-foreground-950 mb-4">分类快捷入口</h2>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              className="px-3.5 py-2 rounded-md bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      {showPlayer && (
        <div
          className="fixed inset-0 z-[60] bg-foreground-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowPlayer(false)}
        >
          <div
            className="w-full max-w-2xl bg-background-50 rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full aspect-video bg-foreground-950 flex flex-col items-center justify-center text-center px-6">
              <span className="w-16 h-16 rounded-full bg-background-50/10 border border-background-50/30 flex items-center justify-center">
                <i className="ri-play-fill text-3xl text-background-50" />
              </span>
              <p className="text-background-50 font-medium mt-4">
                {anime.episodeTitles[currentEpisode] ?? `第 ${currentEpisode + 1} 集`}
              </p>
              <p className="text-background-100/70 text-sm mt-2">
                播放功能待接入接口演示，当前为演示状态，暂无法真实播放
              </p>
            </div>
            <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="font-heading font-semibold text-foreground-950 truncate">{anime.title}</p>
                <p className="text-xs text-foreground-500 mt-1">
                  正在播放：{anime.episodeTitles[currentEpisode] ?? `第 ${currentEpisode + 1} 集`}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setCurrentEpisode((i) => Math.max(0, i - 1))}
                  disabled={currentEpisode === 0}
                  className="h-9 px-3 rounded-md border border-background-200 text-sm text-foreground-600 hover:border-primary-300 disabled:opacity-40 cursor-pointer whitespace-nowrap"
                >
                  上一集
                </button>
                <button
                  onClick={() => setCurrentEpisode((i) => Math.min(anime.episodeTitles.length - 1, i + 1))}
                  disabled={currentEpisode >= anime.episodeTitles.length - 1}
                  className="h-9 px-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 cursor-pointer whitespace-nowrap"
                >
                  下一集
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}